import { useCallback, useEffect, useState, type ReactNode } from 'react'
import { useParams } from 'react-router-dom'
import { lumi } from '@/lib/lumi'
import {Loader2, Lock, Rocket} from 'lucide-react'
import WizardJornadaCompartilhe, { type AcessoCompartilhe } from '@/components/WizardJornadaCompartilhe'
import { normalizarContatos, parseJsonSafe } from '@/utils/jornadaCompartilhe'

export default function JornadaCompartilhe() {
  const { token } = useParams<{ token: string }>()
  const [acesso, setAcesso] = useState<AcessoCompartilhe | null>(null)
  const [fase, setFase] = useState<'carregando' | 'invalido' | 'pronto'>('carregando')

  const carregar = useCallback(async () => {
    if (!token) {
      setFase('invalido')
      return
    }
    try {
      const res = await lumi.entities.jornada_compartilhe_acessos.list({ filter: { token }, limit: 1 })
      const encontrado = res?.list?.[0] as AcessoCompartilhe | undefined
      if (!encontrado?._id) {
        setFase('invalido')
        return
      }
      setAcesso({
        ...encontrado,
        contatosJson: JSON.stringify(normalizarContatos(parseJsonSafe(encontrado.contatosJson, []))),
      })
      setFase('pronto')
    } catch (e) {
      console.error('[jornada-compartilhe] erro ao carregar', e)
      setFase('invalido')
    }
  }, [token])

  useEffect(() => {
    document.title = 'Jornada Compartilhe — Diagnóstico Saúde e Emoções'
    void carregar()
  }, [carregar])

  if (fase === 'carregando') {
    return (
      <TelaBase>
        <Loader2 className="w-8 h-8 text-[#2D6A4F] animate-spin mx-auto mb-3" />
        <p className="text-[#5F5E5A]">Carregando sua Jornada Compartilhe...</p>
      </TelaBase>
    )
  }

  if (fase === 'invalido' || !acesso) {
    return (
      <TelaBase>
        <Lock className="w-10 h-10 text-[#B08968] mx-auto mb-4" />
        <h1 className="font-cormorant text-3xl font-semibold text-[#2C2C2A] mb-3">Link inválido</h1>
        <p className="text-[#5F5E5A] leading-relaxed">
          Este link da Jornada Compartilhe não foi encontrado. Confira com quem enviou este acesso.
        </p>
      </TelaBase>
    )
  }

  return (
    <div className="min-h-screen bg-[#F8F4EF] px-4 py-8">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-8">
          <p className="text-xs uppercase tracking-[0.18em] text-[#B08968] font-semibold mb-2">Diagnóstico Saúde e Emoções</p>
          <div className="inline-flex items-center gap-2 text-[#2D6A4F]">
            <Rocket className="w-5 h-5" />
            <h1 className="font-cormorant text-3xl font-semibold">Jornada Compartilhe</h1>
          </div>
          <p className="text-sm text-[#5F5E5A] mt-2">Olá, {acesso.nomeDestinatario?.split(' ')[0] || 'você'}.</p>
        </div>
        <WizardJornadaCompartilhe
          acesso={acesso}
          modoPublico
          onVoltar={() => {
            window.location.href = 'https://www.descubraseusoleos.com.br/'
          }}
          onAtualizado={setAcesso}
        />
      </div>
    </div>
  )
}

function TelaBase({ children }: { children: ReactNode }) {
  return (
    <div className="min-h-screen bg-[#F8F4EF] flex items-center justify-center px-4 py-10">
      <div className="bg-white rounded-2xl shadow-md p-8 sm:p-10 text-center max-w-md w-full">{children}</div>
    </div>
  )
}
